import { Form, Formik, useFormikContext } from 'formik'
import Link from 'next/link'
import { useRouter } from 'next/router'
import numeral from 'numeral'
import pluralize from 'pluralize'
import { ChangeEvent, useMemo } from 'react'

import { useAuth } from 'src/providers/AuthProvider'
import { useUser } from 'src/providers/UserProvider'

import { ButtonRebrand } from 'src/components/atoms/ButtonRebrand'
import { TypographyRebrand } from 'src/components/atoms/TypographyRebrand'
import { FormField } from 'src/components/molecules/FormField'
import {
  PRICE_PER_TREE,
  calculateArea
} from 'src/components/organisms/CarbonCalculator/helper'
import { DonateFrequencyTab } from 'src/components/organisms/forms/DonateForm/types'

import { businessPackagesPath, donatePath } from 'src/config/paths'
import { FormFieldTypes, Option } from 'src/types/form'
import { trackEvent } from 'src/utils/ga'

const fundingQuantityOptions: Option[] = [
  {
    label: '1',
    value: '1'
  },
  {
    label: '5',
    value: '5'
  },
  {
    label: '10',
    value: '10'
  },
  {
    label: '25',
    value: '25'
  },
  {
    label: '50',
    value: '50'
  },
  {
    label: 'Other',
    value: 'other'
  }
]

const DonateWidgetForm = () => {
  const { setFieldValue, values, submitForm } =
    useFormikContext<DonateFormType>()

  const { isLoading: isUserLoading } = useUser()

  const numberOfTrees = useMemo(() => {
    if (values.NumberOfTrees === 'other') {
      return Number(values.NumberOfTreesOther)
    }
    return Number(values.NumberOfTrees)
  }, [values])

  const amount = useMemo(() => PRICE_PER_TREE * numberOfTrees, [numberOfTrees])

  const area = useMemo(() => calculateArea(numberOfTrees), [numberOfTrees])

  return (
    <Form>
      <div className='bg-white w-full max-w-146 px-7 py-5 flex flex-col gap-8 **:mb-0'>
        <div className='flex flex-wrap justify-between items-center gap-x-8 gap-y-3'>
          <TypographyRebrand variant='h4' color='forestDeep'>
            <span className='uppercase'>Donate</span>
          </TypographyRebrand>
          <FormField
            type={FormFieldTypes.tabSwitchRebrand}
            id='DonationType'
            size='small'
            switchOptions={[
              {
                label: 'Monthly',
                value: DonateFrequencyTab.Monthly
              },
              {
                label: 'One-off',
                value: DonateFrequencyTab.Once
              }
            ]}
            label=''
            className='w-full sm:w-68 mb-0 [&_button]:uppercase [&_button]:whitespace-nowrap'
          />
        </div>
        <div className='flex flex-col gap-8'>
          <div>
            <TypographyRebrand variant='paragraph' color='forestDeep'>
              Number of trees
            </TypographyRebrand>
            <FormField
              id='NumberOfTrees'
              label=''
              labelClassName='text-shark!'
              options={fundingQuantityOptions}
              type={FormFieldTypes.treeQuantityRadioGroupRebrand}
              conditionForConditionalComponent={
                values.NumberOfTrees === 'other'
              }
              conditionalComponent={
                <FormField
                  id='QuantityOther'
                  min={0}
                  label=''
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setFieldValue('NumberOfTreesOther', e.target.value)
                  }}
                  type={FormFieldTypes.number}
                  className='mt-2'
                />
              }
              conditionalComponentId='QuantityOther'
            />
          </div>
          {numberOfTrees > 0 ? (
            <div>
              <TypographyRebrand variant='paragraph' color='forestDeep'>
                <img
                  src='/img/icons/rebranded/land-forest-green.svg'
                  alt=''
                  className='inline mr-2.25 h-7'
                />{' '}
                Could restore {area.formattedValue} {area.unit} of land
              </TypographyRebrand>
            </div>
          ) : null}
          <ButtonRebrand
            variant='lime'
            onClick={submitForm}
            disabled={isUserLoading || !(numberOfTrees > 0)}
          >
            Donate trees ({numeral(amount).format('$0,0')})
          </ButtonRebrand>
          <TypographyRebrand variant='paragraph' color='forestDeep'>
            Want to make more of an impact?{' '}
            <Link className='underline' href={businessPackagesPath()}>
              Explore packages
            </Link>
          </TypographyRebrand>
        </div>
      </div>
    </Form>
  )
}

type DonateFormType = {
  DonationType: DonateFrequencyTab.Once | DonateFrequencyTab.Monthly
  NumberOfTrees: string
  NumberOfTreesOther?: string
}

const DonateWidget = () => {
  const router = useRouter()
  const { user } = useUser()
  const { signupWithCustomRedirect } = useAuth()

  const handleDonateSubmit = (values: DonateFormType) => {
    const isOtherAmount = values.NumberOfTrees === 'other'
    const quantity = isOtherAmount
      ? values.NumberOfTreesOther
      : values.NumberOfTrees

    const presetItemLabel = `${values.NumberOfTrees} ${pluralize(
      'tree',
      Number(values.NumberOfTrees)
    )}`

    trackEvent('add_to_cart', {
      items: [
        {
          item_id: isOtherAmount ? 'trees-other' : presetItemLabel,
          item_name: isOtherAmount ? 'Other amount' : presetItemLabel,
          item_list_name: 'Tree',
          item_category: 'Donate',
          quantity,
          price: PRICE_PER_TREE
        }
      ]
    })

    const paymentStepPath = donatePath({
      queryParams: {
        quantity,
        donationType: values.DonationType,
        stepIndex: 1
      }
    })

    if (values.DonationType === DonateFrequencyTab.Monthly && !user) {
      return signupWithCustomRedirect(paymentStepPath)
    }

    return router.push(paymentStepPath)
  }

  return (
    <Formik<DonateFormType>
      initialValues={{
        DonationType: DonateFrequencyTab.Monthly,
        NumberOfTrees: '5'
      }}
      onSubmit={handleDonateSubmit}
    >
      <DonateWidgetForm />
    </Formik>
  )
}

export default DonateWidget
